import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, getApiEmpresa } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, PencilSimple, Trash, Copy, UsersThree, FileArrowUp, DownloadSimple, ArrowsClockwise, MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { hoyISO } from "@/lib/datetime";
import { useAuth, canManage } from "@/contexts/AuthContext";
import { useEmpresa } from "@/contexts/EmpresaContext";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";

// Carga horaria de los docentes (solo escuelas, ver ROUTE_ACCESS.cargaDocente).
// La carga se edita completa en memoria y se guarda de una vez: el backend la valida
// (traslapes, módulos, materias) y versiona si la vigencia es posterior a la actual.

const DIAS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"]; // 0=domingo, como el backend
const DIAS_GRID = [1, 2, 3, 4, 5, 6];
const SIN_GRUPO = "__sin_grupo__";

const TOUR_STORAGE_KEY = "chkr_tour_carga_docente_seen";

const TOUR_STEPS = [
  { target: "[data-tour='carga-ciclo']", title: "Ciclo escolar", content: "La carga se captura por ciclo. Elige aquí el ciclo; si ya tienes uno anterior, \"Copiar de otro ciclo\" trae las cargas de todos los docentes para no capturarlas de nuevo." },
  { target: "[data-tour='carga-importar']", title: "Importar desde Excel", content: "Para muchos docentes a la vez: descarga la plantilla, llena una fila por clase (número de empleado, día, módulo, materia, grupo) y súbela. Antes de guardar verás los errores, como un docente o un módulo que no existe." },
  { target: "[data-tour='carga-docentes']", title: "Docentes", content: "Aquí salen los empleados marcados como Docente en Empleados, con sus horas a la semana. Elige uno para ver o capturar su carga." },
  { target: "[data-tour='carga-editor']", title: "Cuadrícula de clases", content: "Las filas son los módulos de Escuela → Módulos y las columnas los días. Da clic en una celda para poner la materia y el grupo. \"Copiar día\" y \"Duplicar de otro docente\" ahorran captura; \"Horario libre\" sirve para clases fuera de los módulos o para horas administrativas." },
  { target: "[data-tour='carga-editor']", title: "Guardar y cambios a mitad de ciclo", content: "Al guardar eliges desde qué fecha vale la carga. Si cambia a mitad de ciclo, guarda con la fecha nueva: la carga anterior se conserva para los reportes de las fechas pasadas." },
];

const minutos = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};
const horas = (clases, tipo) =>
  Math.round(clases.filter((c) => c.tipo === tipo).reduce((t, c) => t + minutos(c.hora_fin) - minutos(c.hora_inicio), 0) / 6) / 10;
const nuevoId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

function errorDetalle(e) {
  const d = e.response?.data?.detail;
  if (d && Array.isArray(d.errores)) return { msg: d.msg, errores: d.errores };
  return { msg: formatApiError(d), errores: [] };
}

// ---------------- diálogo de un bloque ----------------
function BloqueDialog({ bloque, onClose, onSave, materias, grupos, centros, libre }) {
  const [f, setF] = useState(bloque);
  useEffect(() => setF(bloque), [bloque]);
  if (!f) return null;
  const set = (k, v) => setF({ ...f, [k]: v });
  const gruposCentro = grupos.filter((g) => !g.centro_id || g.centro_id === f.centro_id);
  return (
    <Dialog open={!!bloque} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">
            {libre ? "Bloque con horario libre" : `${DIAS[f.dia]} · ${f.hora_inicio}–${f.hora_fin}`}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="label-uppercase">Tipo</Label>
            <Select value={f.tipo} onValueChange={(v) => set("tipo", v)}>
              <SelectTrigger data-testid="bloque-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="clase">Clase</SelectItem>
                <SelectItem value="administrativo">Administrativo (guardia, coordinación…)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {libre && (
            <>
              <div className="space-y-1">
                <Label className="label-uppercase">Día</Label>
                <Select value={String(f.dia)} onValueChange={(v) => set("dia", Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{[1, 2, 3, 4, 5, 6, 0].map((d) => <SelectItem key={d} value={String(d)}>{DIAS[d]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="label-uppercase">Centro</Label>
                <Select value={f.centro_id} onValueChange={(v) => set("centro_id", v)}>
                  <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>{centros.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Inicio</Label>
                <Input type="time" value={f.hora_inicio} onChange={(e) => set("hora_inicio", e.target.value)} data-testid="bloque-inicio" />
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Fin</Label>
                <Input type="time" value={f.hora_fin} onChange={(e) => set("hora_fin", e.target.value)} data-testid="bloque-fin" />
              </div>
            </>
          )}
          {f.tipo === "clase" ? (
            <div className="space-y-1 sm:col-span-2">
              <Label className="label-uppercase">Materia</Label>
              <Select value={f.materia_id || ""} onValueChange={(v) => set("materia_id", v)}>
                <SelectTrigger data-testid="bloque-materia"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>{materias.map((m) => <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-1 sm:col-span-2">
              <Label className="label-uppercase">Actividad</Label>
              <Input value={f.actividad || ""} onChange={(e) => set("actividad", e.target.value)} placeholder="Guardia, coordinación, tutoría…" />
            </div>
          )}
          <div className="space-y-1">
            <Label className="label-uppercase">Grupo</Label>
            <Select value={f.grupo_id || SIN_GRUPO} onValueChange={(v) => set("grupo_id", v === SIN_GRUPO ? null : v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={SIN_GRUPO}>Sin grupo</SelectItem>
                {gruposCentro.map((g) => <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="label-uppercase">Aula</Label>
            <Input value={f.aula || ""} onChange={(e) => set("aula", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            className="bg-brand hover:bg-brand-hover"
            data-testid="bloque-aceptar"
            onClick={() => {
              if (f.tipo === "clase" && !f.materia_id) return toast.error("Elige la materia");
              if (libre && (!f.hora_inicio || !f.hora_fin || f.hora_fin <= f.hora_inicio)) return toast.error("Revisa las horas");
              onSave(f);
            }}
          >
            Aceptar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- importación ----------------
function ImportarDialog({ open, onClose, ciclo, onDone }) {
  const [archivo, setArchivo] = useState(null);
  const [vigencia, setVigencia] = useState("");
  const [crear, setCrear] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setArchivo(null);
      setPreview(null);
      setCrear(false);
      setVigencia(ciclo?.fecha_inicio || "");
    }
  }, [open, ciclo]);

  const enviar = async (confirmar) => {
    if (!archivo) return toast.error("Elige un archivo");
    const fd = new FormData();
    fd.append("archivo", archivo);
    fd.append("ciclo_id", ciclo.id);
    if (vigencia) fd.append("vigencia_desde", vigencia);
    fd.append("confirmar", confirmar ? "true" : "false");
    fd.append("crear_catalogos", crear ? "true" : "false");
    if (getApiEmpresa()) fd.append("empresa_id", getApiEmpresa());
    setBusy(true);
    try {
      const { data } = await api.post("/escuela/cargas/importar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (confirmar) {
        toast.success(`Carga importada para ${data.docentes_guardados} docente(s)`);
        onDone();
        onClose();
      } else {
        setPreview(data);
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && d.docentes) setPreview(d);
      toast.error(d?.msg || formatApiError(d));
    } finally {
      setBusy(false);
    }
  };

  const descargarPlantilla = async () => {
    try {
      const { data } = await api.get("/escuela/cargas/plantilla.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_carga_docente.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo descargar la plantilla");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="importar-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Importar carga desde Excel · {ciclo?.nombre}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Una fila por clase. Cada docente que aparezca en el archivo queda con exactamente esas clases.{" "}
          <button className="text-brand-accent underline font-semibold" onClick={descargarPlantilla} data-testid="descargar-plantilla">
            Descargar plantilla
          </button>{" "}
          (incluye tus módulos, materias y grupos).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div className="space-y-1">
            <Label className="label-uppercase">Archivo (.xlsx o .csv)</Label>
            <Input type="file" accept=".xlsx,.csv" onChange={(e) => { setArchivo(e.target.files?.[0] || null); setPreview(null); }} data-testid="importar-archivo" />
          </div>
          <div className="space-y-1">
            <Label className="label-uppercase">Vigente desde</Label>
            <Input type="date" min={ciclo?.fecha_inicio} max={ciclo?.fecha_fin} value={vigencia} onChange={(e) => { setVigencia(e.target.value); setPreview(null); }} />
          </div>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={crear} onChange={(e) => { setCrear(e.target.checked); setPreview(null); }} data-testid="importar-crear" />
            Dar de alta las materias y grupos que no existan
          </label>
        </div>

        {preview && (
          <div className="mt-4 space-y-4" data-testid="importar-preview">
            <div className="text-sm text-slate-700">
              {preview.filas} fila(s) · {preview.docentes.length} docente(s)
              {preview.errores.length > 0 && <span className="text-red-700 font-semibold"> · {preview.errores.length} error(es)</span>}
            </div>
            {preview.errores.length > 0 && (
              <div className="border border-red-200 bg-red-50 rounded-md p-3 max-h-48 overflow-y-auto">
                <ul className="text-xs text-red-800 space-y-1">
                  {preview.errores.map((e, i) => <li key={i}>{e.fila ? `Fila ${e.fila}: ` : ""}{e.error}</li>)}
                </ul>
              </div>
            )}
            {(preview.crear.materias.length > 0 || preview.crear.grupos.length > 0) && (
              <div className="text-xs text-slate-600">
                Se darán de alta:{" "}
                {[...preview.crear.materias.map((m) => `materia ${m}`), ...preview.crear.grupos.map((g) => `grupo ${g}`)].join(", ")}
              </div>
            )}
            {preview.docentes.length > 0 && (
              <div className="border border-slate-200 rounded-md overflow-x-auto">
                <table className="data-table">
                  <thead><tr><th>Docente</th><th>Clases</th><th>Horas clase</th><th>Horas admin.</th><th></th></tr></thead>
                  <tbody>
                    {preview.docentes.map((d) => (
                      <tr key={d.empleado_id}>
                        <td><div className="font-semibold">{d.nombre}</div><div className="text-xs text-slate-500 font-mono">{d.numero_empleado}</div></td>
                        <td>{d.num_clases}</td>
                        <td>{d.horas_clase}</td>
                        <td>{d.horas_administrativas}</td>
                        <td className="text-xs text-amber-700">{d.reemplaza_carga ? "Reemplaza su carga actual" : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          {!preview || preview.errores.length > 0 ? (
            <Button onClick={() => enviar(false)} disabled={busy || !archivo} className="bg-brand hover:bg-brand-hover" data-testid="importar-revisar">
              {busy ? "Revisando…" : "Revisar archivo"}
            </Button>
          ) : (
            <Button onClick={() => enviar(true)} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="importar-confirmar">
              {busy ? "Guardando…" : `Importar ${preview.docentes.length} docente(s)`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- página ----------------
export default function CargaDocente() {
  const { user } = useAuth();
  const { empresaActivaId } = useEmpresa();
  const editable = canManage(user);

  const [ciclos, setCiclos] = useState([]);
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY, ciclos.length > 0);
  const [cicloId, setCicloId] = useState("");
  const [docentes, setDocentes] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [cat, setCat] = useState({ centros: [], modulos: [], materias: [], grupos: [] });
  const [sel, setSel] = useState(null); // empleado_id
  const [detalle, setDetalle] = useState(null);
  const [clases, setClases] = useState([]);
  const [vigencia, setVigencia] = useState("");
  const [sucio, setSucio] = useState(false);
  const [vista, setVista] = useState(""); // "centro_id|turno" de la cuadrícula
  const [bloque, setBloque] = useState(null);
  const [bloqueLibre, setBloqueLibre] = useState(false);
  const [errores, setErrores] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copiarDia, setCopiarDia] = useState(null);
  const [duplicarOpen, setDuplicarOpen] = useState(false);
  const [duplicarDe, setDuplicarDe] = useState("");
  const [importarOpen, setImportarOpen] = useState(false);
  const [copiaCiclo, setCopiaCiclo] = useState(null);

  const ciclo = ciclos.find((c) => c.id === cicloId);

  useEffect(() => {
    Promise.all([
      api.get("/escuela/ciclos"), api.get("/centros"), api.get("/escuela/modulos"),
      api.get("/escuela/materias"), api.get("/escuela/grupos"),
    ]).then(([ci, ce, mo, ma, gr]) => {
      setCiclos(ci.data);
      const hoy = hoyISO();
      const actual = ci.data.find((c) => c.fecha_inicio <= hoy && hoy <= c.fecha_fin) || ci.data[0];
      setCicloId(actual?.id || "");
      setCat({ centros: ce.data.filter((c) => c.activo !== false), modulos: mo.data, materias: ma.data, grupos: gr.data });
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [empresaActivaId]);

  const cargarDocentes = () =>
    cicloId && api.get("/escuela/cargas", { params: { ciclo_id: cicloId } }).then((r) => setDocentes(r.data));
  useEffect(() => {
    cargarDocentes();
    setSel(null);
    setDetalle(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cicloId]);

  const recargarCatalogos = () =>
    Promise.all([api.get("/escuela/materias"), api.get("/escuela/grupos")]).then(([ma, gr]) =>
      setCat((c) => ({ ...c, materias: ma.data, grupos: gr.data })));

  const vistas = useMemo(() => {
    const vistos = new Map();
    cat.modulos.forEach((m) => {
      const k = `${m.centro_id}|${m.turno}`;
      if (!vistos.has(k)) vistos.set(k, `${cat.centros.find((c) => c.id === m.centro_id)?.nombre || "—"} · ${m.turno}`);
    });
    return [...vistos.entries()];
  }, [cat]);

  const abrir = async (empleadoId) => {
    if (sucio && !window.confirm("Hay cambios sin guardar en la carga actual. ¿Descartarlos?")) return;
    const { data } = await api.get(`/escuela/cargas/${empleadoId}`, { params: { ciclo_id: cicloId } });
    setSel(empleadoId);
    setDetalle(data);
    setClases(data.vigente?.clases || []);
    setVigencia(data.vigente?.vigencia_desde || ciclo?.fecha_inicio || "");
    setSucio(false);
    setErrores([]);
    const propia = vistas.find(([k]) => k.startsWith(`${data.empleado.centro_id}|`));
    setVista((propia || vistas[0] || [""])[0]);
  };

  const cambiar = (nuevas) => {
    setClases(nuevas);
    setSucio(true);
  };

  const [vistaCentro, vistaTurno] = vista.split("|");
  const filas = cat.modulos.filter((m) => m.centro_id === vistaCentro && m.turno === vistaTurno);
  const idsFilas = new Set(filas.map((m) => m.id));
  const fueraDeVista = clases.filter((c) => !c.modulo_id || !idsFilas.has(c.modulo_id));
  const nombreMateria = (id) => cat.materias.find((m) => m.id === id)?.nombre || "—";
  const nombreGrupo = (id) => cat.grupos.find((g) => g.id === id)?.nombre;
  const nombreCentro = (id) => cat.centros.find((c) => c.id === id)?.nombre || "—";

  const clickCelda = (modulo, dia) => {
    if (!editable || modulo.tipo === "receso") return;
    const actual = clases.find((c) => c.modulo_id === modulo.id && c.dia === dia);
    setBloqueLibre(false);
    setBloque(actual || {
      id: nuevoId(), dia, centro_id: modulo.centro_id, tipo: "clase", modulo_id: modulo.id,
      hora_inicio: modulo.hora_inicio, hora_fin: modulo.hora_fin, materia_id: null, grupo_id: null, aula: "", actividad: "",
    });
  };

  const guardarBloque = (b) => {
    const resto = clases.filter((c) => c.id !== b.id);
    cambiar([...resto, b]);
    setBloque(null);
  };

  const quitarBloque = (id) => cambiar(clases.filter((c) => c.id !== id));

  const aplicarCopiaDia = () => {
    const { desde, hacia } = copiarDia;
    if (!hacia.length) return toast.error("Elige al menos un día destino");
    const origen = clases.filter((c) => c.dia === desde);
    const sinDestino = clases.filter((c) => !hacia.includes(c.dia));
    const copias = hacia.flatMap((dia) => origen.map((c) => ({ ...c, id: nuevoId(), dia })));
    cambiar([...sinDestino, ...copias]);
    setCopiarDia(null);
    toast.success(`${DIAS[desde]} copiado a ${hacia.map((d) => DIAS[d]).join(", ")}`);
  };

  const aplicarDuplicado = async () => {
    if (!duplicarDe) return;
    const { data } = await api.get(`/escuela/cargas/${duplicarDe}`, { params: { ciclo_id: cicloId } });
    const origen = data.vigente?.clases || [];
    if (!origen.length) return toast.error("Ese docente no tiene carga en este ciclo");
    cambiar(origen.map((c) => ({ ...c, id: nuevoId() })));
    setDuplicarOpen(false);
    toast.success(`Se copiaron ${origen.length} bloques de ${data.empleado.nombre}. Revisa y guarda.`);
  };

  const guardar = async () => {
    setBusy(true);
    setErrores([]);
    try {
      const { data } = await api.put(`/escuela/cargas/${sel}`, { ciclo_id: cicloId, vigencia_desde: vigencia, clases });
      toast.success("Carga guardada");
      data.advertencias.forEach((a) => toast.warning(a, { duration: 8000 }));
      setSucio(false);
      cargarDocentes();
      const r = await api.get(`/escuela/cargas/${sel}`, { params: { ciclo_id: cicloId } });
      setDetalle(r.data);
      setClases(r.data.vigente?.clases || []);
    } catch (e) {
      const { msg, errores: errs } = errorDetalle(e);
      setErrores(errs);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const ejecutarCopiaCiclo = async () => {
    try {
      const { data } = await api.post("/escuela/cargas/copiar-ciclo", {
        ciclo_origen_id: copiaCiclo.origen, ciclo_destino_id: cicloId, sobrescribir: copiaCiclo.sobrescribir,
        empresa_id: getApiEmpresa() || undefined,
      });
      toast.success(`Copiadas: ${data.copiados.length} · omitidas (ya tenían carga): ${data.omitidos.length}`);
      data.errores.forEach((e) => toast.error(`${e.docente}: ${e.errores.join("; ")}`, { duration: 10000 }));
      setCopiaCiclo(null);
      cargarDocentes();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const docentesFiltrados = docentes.filter((d) =>
    !busqueda || `${d.nombre} ${d.numero_empleado}`.toLowerCase().includes(busqueda.toLowerCase()));
  const vigenteDesde = detalle?.vigente?.vigencia_desde;

  if (ciclos.length === 0) {
    return (
      <div className="p-6 md:p-10" data-testid="carga-docente-page">
        <PageHeader kicker="Escuela" title="Carga docente" />
        <div className="bg-white border border-slate-200 rounded-md p-8 text-slate-600">
          Primero da de alta un ciclo escolar, los módulos y las materias en <strong>Escuela</strong>.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10" data-testid="carga-docente-page">
      <PageHeader
        kicker="Escuela"
        title="Carga docente"
        subtitle="Clases de cada docente por ciclo escolar. La asistencia y el pago por hora-clase se calculan contra esta carga."
        actions={
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={cicloId} onValueChange={(v) => {
              if (sucio && !window.confirm("Hay cambios sin guardar. ¿Descartarlos?")) return;
              setSucio(false);
              setCicloId(v);
            }}>
              <SelectTrigger className="w-40" data-testid="ciclo-select" data-tour="carga-ciclo"><SelectValue /></SelectTrigger>
              <SelectContent>{ciclos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
            </Select>
            {editable && (
              <>
                <Button variant="outline" onClick={() => setImportarOpen(true)} data-testid="importar-btn" data-tour="carga-importar">
                  <FileArrowUp size={16} className="mr-2" /> Importar Excel
                </Button>
                {ciclos.length > 1 && (
                  <Button variant="outline" onClick={() => setCopiaCiclo({ origen: ciclos.find((c) => c.id !== cicloId)?.id || "", sobrescribir: false })} data-testid="copiar-ciclo-btn">
                    <ArrowsClockwise size={16} className="mr-2" /> Copiar de otro ciclo
                  </Button>
                )}
              </>
            )}
            <TourHelpButton onClick={startTour} />
          </div>
        }
      />
      <GuidedTour steps={TOUR_STEPS} active={tourActive} onFinish={finishTour} />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Docentes */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden self-start" data-tour="carga-docentes">
          <div className="p-3 border-b border-slate-200 relative">
            <MagnifyingGlass size={14} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-8" placeholder="Buscar docente" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-100" data-testid="docentes-lista">
            {docentesFiltrados.length === 0 && (
              <div className="p-4 text-sm text-slate-500">
                Sin docentes. Marca al personal como <strong>Docente</strong> en Empleados.
              </div>
            )}
            {docentesFiltrados.map((d) => (
              <button
                key={d.id}
                onClick={() => abrir(d.id)}
                data-testid={`docente-${d.id}`}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${sel === d.id ? "bg-brand-accent-soft" : ""}`}
              >
                <div className="font-semibold text-sm text-ink">{d.nombre}</div>
                <div className="text-xs text-slate-500">
                  {d.numero_empleado} ·{" "}
                  {d.carga ? `${d.carga.horas_clase} h clase${d.carga.horas_administrativas ? ` + ${d.carga.horas_administrativas} h admin.` : ""}` : <span className="text-amber-700">sin carga</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Editor */}
        {!detalle ? (
          <div className="bg-white border border-slate-200 rounded-md p-8 text-slate-500 text-sm" data-tour="carga-editor">Elige un docente para ver o capturar su carga.</div>
        ) : (
          <div className="space-y-4 min-w-0" data-tour="carga-editor">
            <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap gap-4 items-end justify-between">
              <div>
                <div className="font-display text-xl font-bold text-ink">{detalle.empleado.nombre}</div>
                <div className="text-sm text-slate-600" data-testid="carga-totales">
                  {horas(clases, "clase")} h clase · {horas(clases, "administrativo")} h administrativas · {clases.filter((c) => c.tipo === "clase").length} clases a la semana
                  {sucio && <span className="ml-2 text-amber-700 font-semibold">· sin guardar</span>}
                </div>
              </div>
              {editable && (
                <div className="flex gap-2 flex-wrap items-end">
                  <div className="space-y-1">
                    <Label className="label-uppercase">Vigente desde</Label>
                    <Input type="date" min={ciclo?.fecha_inicio} max={ciclo?.fecha_fin} value={vigencia} onChange={(e) => setVigencia(e.target.value)} className="w-40" data-testid="carga-vigencia" />
                  </div>
                  <Button onClick={guardar} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="carga-guardar">
                    {busy ? "Guardando…" : "Guardar carga"}
                  </Button>
                </div>
              )}
            </div>
            {editable && vigenteDesde && vigencia > vigenteDesde && (
              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                Se creará una versión nueva desde el {vigencia}. La carga vigente desde el {vigenteDesde} se conserva para los reportes de las fechas anteriores.
              </div>
            )}
            {errores.length > 0 && (
              <div className="border border-red-200 bg-red-50 rounded-md p-3" data-testid="carga-errores">
                <ul className="text-sm text-red-800 list-disc pl-5 space-y-1">{errores.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </div>
            )}

            <div className="flex flex-wrap gap-2 items-center">
              {vistas.length > 0 && (
                <Select value={vista} onValueChange={setVista}>
                  <SelectTrigger className="w-64" data-testid="vista-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{vistas.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {editable && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setCopiarDia({ desde: 1, hacia: [] })} data-testid="copiar-dia-btn">
                    <Copy size={14} className="mr-1" /> Copiar día
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setDuplicarDe(""); setDuplicarOpen(true); }} data-testid="duplicar-btn">
                    <UsersThree size={14} className="mr-1" /> Duplicar de otro docente
                  </Button>
                  <Button variant="outline" size="sm" data-testid="bloque-libre-btn" onClick={() => {
                    setBloqueLibre(true);
                    setBloque({ id: nuevoId(), dia: 1, centro_id: detalle.empleado.centro_id, tipo: "clase", modulo_id: null,
                      hora_inicio: "", hora_fin: "", materia_id: null, grupo_id: null, aula: "", actividad: "" });
                  }}>
                    <Plus size={14} className="mr-1" /> Horario libre
                  </Button>
                </>
              )}
            </div>

            {filas.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-md p-6 text-sm text-slate-500">
                No hay módulos dados de alta. Créalos en Escuela → Módulos, o captura con "Horario libre".
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="carga-grid">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="text-left px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 w-36">Módulo</th>
                      {DIAS_GRID.map((d) => <th key={d} className="px-2 py-2 text-xs font-bold uppercase tracking-wider text-slate-500">{DIAS[d]}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((m) => (
                      <tr key={m.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 align-top">
                          <div className="font-semibold text-ink">{m.nombre}</div>
                          <div className="text-xs text-slate-500 font-mono">{m.hora_inicio}–{m.hora_fin}</div>
                        </td>
                        {DIAS_GRID.map((d) => {
                          if (m.tipo === "receso") {
                            return <td key={d} className="bg-slate-100 text-center text-xs text-slate-400">receso</td>;
                          }
                          const c = clases.find((x) => x.modulo_id === m.id && x.dia === d);
                          return (
                            <td key={d} className="p-1 align-top min-w-[110px]">
                              <div
                                role={editable ? "button" : undefined}
                                onClick={() => clickCelda(m, d)}
                                data-testid={`celda-${m.id}-${d}`}
                                className={`group relative rounded px-2 py-1.5 min-h-[44px] text-xs ${
                                  c
                                    ? c.tipo === "clase" ? "bg-brand text-white" : "bg-brand-accent-soft text-brand-accent"
                                    : editable ? "border border-dashed border-slate-200 hover:border-brand-accent hover:bg-slate-50 cursor-pointer" : ""
                                } ${c && editable ? "cursor-pointer" : ""}`}
                              >
                                {c && (
                                  <>
                                    <div className="font-semibold leading-tight">{c.tipo === "clase" ? nombreMateria(c.materia_id) : c.actividad || "Administrativo"}</div>
                                    <div className="opacity-80">{[nombreGrupo(c.grupo_id), c.aula && `Aula ${c.aula}`].filter(Boolean).join(" · ")}</div>
                                    {editable && (
                                      <button
                                        className="absolute top-0.5 right-0.5 hidden group-hover:block opacity-80 hover:opacity-100"
                                        onClick={(e) => { e.stopPropagation(); quitarBloque(c.id); }}
                                        title="Quitar"
                                      >
                                        <Trash size={12} />
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {fueraDeVista.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-50">
                  Otros planteles, turnos y horarios libres
                </div>
                <table className="data-table">
                  <tbody>
                    {[...fueraDeVista].sort((a, b) => a.dia - b.dia || a.hora_inicio.localeCompare(b.hora_inicio)).map((c) => (
                      <tr key={c.id}>
                        <td className="whitespace-nowrap">{DIAS[c.dia]} · {c.hora_inicio}–{c.hora_fin}</td>
                        <td>{nombreCentro(c.centro_id)}</td>
                        <td>{c.tipo === "clase" ? nombreMateria(c.materia_id) : `${c.actividad || "Administrativo"} (admin.)`}</td>
                        <td>{[nombreGrupo(c.grupo_id), c.aula && `Aula ${c.aula}`].filter(Boolean).join(" · ")}</td>
                        <td className="whitespace-nowrap">
                          {editable && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => { setBloqueLibre(!c.modulo_id); setBloque(c); }}><PencilSimple size={16} /></Button>
                              <Button variant="ghost" size="sm" onClick={() => quitarBloque(c.id)}><Trash size={16} className="text-red-600" /></Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {detalle.versiones.length > 1 && (
              <div className="text-xs text-slate-500">
                Versiones en este ciclo:{" "}
                {detalle.versiones.map((v) => `${v.vigencia_desde}${v.vigencia_hasta ? ` a ${v.vigencia_hasta}` : " (vigente)"} · ${v.num_clases} clases`).join(" | ")}
              </div>
            )}
          </div>
        )}
      </div>

      <BloqueDialog
        bloque={bloque}
        libre={bloqueLibre}
        onClose={() => setBloque(null)}
        onSave={guardarBloque}
        materias={cat.materias}
        grupos={cat.grupos}
        centros={cat.centros}
      />

      {/* Copiar día */}
      <Dialog open={!!copiarDia} onOpenChange={(o) => !o && setCopiarDia(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Copiar día</DialogTitle></DialogHeader>
          {copiarDia && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="label-uppercase">Copiar las clases del</Label>
                <Select value={String(copiarDia.desde)} onValueChange={(v) => setCopiarDia({ desde: Number(v), hacia: copiarDia.hacia.filter((d) => d !== Number(v)) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{[1, 2, 3, 4, 5, 6, 0].map((d) => <SelectItem key={d} value={String(d)}>{DIAS[d]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase">a los días (reemplaza lo que tengan)</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {[1, 2, 3, 4, 5, 6, 0].filter((d) => d !== copiarDia.desde).map((d) => (
                    <label key={d} className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={copiarDia.hacia.includes(d)}
                        onChange={(e) => setCopiarDia({ ...copiarDia, hacia: e.target.checked ? [...copiarDia.hacia, d] : copiarDia.hacia.filter((x) => x !== d) })}
                        data-testid={`copiar-a-${d}`}
                      />
                      {DIAS[d]}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopiarDia(null)}>Cancelar</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={aplicarCopiaDia} data-testid="copiar-dia-aplicar">Copiar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicar de otro docente */}
      <Dialog open={duplicarOpen} onOpenChange={setDuplicarOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Duplicar la carga de otro docente</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Reemplaza lo capturado aquí con la carga de otro docente en este ciclo. No se guarda hasta que presiones "Guardar carga".</p>
          <Select value={duplicarDe} onValueChange={setDuplicarDe}>
            <SelectTrigger data-testid="duplicar-select"><SelectValue placeholder="Elige un docente" /></SelectTrigger>
            <SelectContent>
              {docentes.filter((d) => d.id !== sel && d.carga?.num_clases).map((d) => <SelectItem key={d.id} value={d.id}>{d.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicarOpen(false)}>Cancelar</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={aplicarDuplicado} disabled={!duplicarDe} data-testid="duplicar-aplicar">Duplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copiar de otro ciclo */}
      <Dialog open={!!copiaCiclo} onOpenChange={(o) => !o && setCopiaCiclo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Copiar cargas a {ciclo?.nombre}</DialogTitle></DialogHeader>
          {copiaCiclo && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="label-uppercase">Desde el ciclo</Label>
                <Select value={copiaCiclo.origen} onValueChange={(v) => setCopiaCiclo({ ...copiaCiclo, origen: v })}>
                  <SelectTrigger><SelectValue placeholder="Elige" /></SelectTrigger>
                  <SelectContent>{ciclos.filter((c) => c.id !== cicloId).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={copiaCiclo.sobrescribir} onChange={(e) => setCopiaCiclo({ ...copiaCiclo, sobrescribir: e.target.checked })} />
                Reemplazar también a los docentes que ya tienen carga en {ciclo?.nombre}
              </label>
              <p className="text-xs text-slate-500">Se copia la última carga de cada docente, vigente desde el inicio de {ciclo?.nombre} ({ciclo?.fecha_inicio}).</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopiaCiclo(null)}>Cancelar</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={ejecutarCopiaCiclo} disabled={!copiaCiclo?.origen} data-testid="copiar-ciclo-aplicar">
              <DownloadSimple size={16} className="mr-2" /> Copiar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportarDialog
        open={importarOpen}
        onClose={() => setImportarOpen(false)}
        ciclo={ciclo}
        onDone={() => { cargarDocentes(); recargarCatalogos(); if (sel) abrir(sel); }}
      />
    </div>
  );
}
